import React from 'react';
import { useAuth } from '@/lib';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { Logout, ContentCopy } from '@mui/icons-material';

export const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [showCopyToast, setShowCopyToast] = React.useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      // The logout will redirect to login page automatically
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleCopyUserId = async () => {
    if (!user) return;
    try {
      await navigator.clipboard.writeText(user.id);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 3000);
    } catch (error) {
      console.error('Failed to copy user ID:', error);
    }
  };

  if (!user) {
    return null;
  }

  const getUserInitials = () => {
    if (user.displayName) {
      return user.displayName
        .split(' ')
        .map((name) => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <Box className="min-h-screen bg-background-dark p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <Box className="text-center mb-8">
          <Typography variant="h4" component="h1" className="text-white mb-2">
            Settings
          </Typography>
          <Typography variant="body1" className="text-gray-400">
            Manage your account and preferences
          </Typography>
        </Box>

        {/* User Profile Card */}
        <Card className="bg-background-surface border border-border">
          <CardContent className="p-6">
            <Box className="flex items-center space-x-4 mb-4">
              <Avatar
                src={user.photoURL || undefined}
                alt={user.displayName || user.email || 'User'}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: 'primary.main',
                  border: '2px solid',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  fontSize: '24px',
                  fontWeight: 600,
                }}
              >
                {getUserInitials()}
              </Avatar>
              <Box className="flex-1">
                <Typography variant="h6" className="text-white mb-1">
                  {user.displayName || 'User'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {user.email}
                </Typography>
                <Tooltip title="Copy user ID" arrow>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'text.disabled',
                        '& .copy-icon': {
                          color: 'text.disabled',
                          opacity: 1,
                        },
                      },
                    }}
                    onClick={handleCopyUserId}
                  >
                    ID: {user.id}
                    <ContentCopy
                      className="copy-icon"
                      sx={{
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                        color: 'text.secondary',
                        fontSize: '16px',
                      }}
                    />
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Logout Button */}
        <Button
          onClick={handleLogout}
          variant="outlined"
          color="error"
          startIcon={<Logout />}
          fullWidth
          size="large"
          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
        >
          Sign Out
        </Button>

        {/* App Version */}
        <Box className="text-center">
          <Typography variant="body2" className="text-gray-500">
            Orbit v1.0.0
          </Typography>
        </Box>
      </div>

      {/* Copy Toast */}
      <Snackbar
        open={showCopyToast}
        autoHideDuration={3000}
        onClose={() => setShowCopyToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowCopyToast(false)}
          severity="success"
          sx={{
            width: '100%',
            backgroundColor: 'info',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            },
          }}
        >
          User ID copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
